<template>
  <div
    class="zm-form-item"
    :class="[
      {
        'zm-form-item--feedback': ZmForm && ZmForm.statusIcon,
        'is-error': validateState === 'error',
        'is-validating': validateState === 'validating',
        'is-success': validateState === 'success',
        'is-required': isRequired || required,
        'is-no-asterisk': ZmForm && ZmForm.hideRequiredAsterisk
      },
      sizeClass ? 'zm-form-item--' + sizeClass : ''
    ]"
  >
    <div class="zm-form-item__header" :style="widthStyle" v-if="ZmForm && ZmForm.labelPosition !== 'inline' && (label || $slots.label)">
      <label
        :for="labelFor"
        class="zm-form-item__label"
      >
        <slot name="label">{{label + form.labelSuffix}}</slot>
        <span
          aria-hidden="true"
          class="required-asterisk"
          v-if="showRequiredAsterisk"
        >*</span>
      </label>
      <slot name="label-suffix"></slot>
    </div>

    <div class="zm-form-item__content" :style="contentStyle">
      <slot></slot>
      <transition name="zm-zoom-in-top">
        <slot
          v-if="isShowErrorMessage"
          name="error"
          :error="validateMessage"
        >
          <div
            class="zm-form-item__error"
            role="alert"
            :aria-label="`${(isRequired || required) ? 'Required item, ': ''} ${label+form.labelSuffix}, ${validateMessage}`"
            :class="{
              'zm-form-item__error--inline': typeof inlineMessage === 'boolean'
                ? inlineMessage
                : (ZmForm && ZmForm.inlineMessage || false)
            }"
          >
            <span aria-hidden="true">
              {{validateMessage}}
            </span>
          </div>
        </slot>
      </transition>
    </div>
  </div>
</template>
<script>
  import AsyncValidator from 'async-validator';
  import emitter from '../../mixins/emitter';
  import objectAssign from '../../utils/merge';
  import { noop, getPropByPath } from '../../utils/util';

  export default {
    name: 'ZmFormItem',

    componentName: 'ZmFormItem',

    mixins: [emitter],

    provide() {
      return {
        ZmFormItem: this
      };
    },

    inject: ['ZmForm'],

    props: {
      label: {
        type: String,
        default: ''
      },
      labelWidth: String,
      prop: String,
      required: {
        type: Boolean,
        default: undefined
      },
      rules: [Object, Array],
      error: String,
      validateStatus: String,
      for: String,
      inlineMessage: {
        type: [String, Boolean],
        default: ''
      },
      showMessage: {
        type: Boolean,
        default: true
      }
      // size: String
    },
    watch: {
      error: {
        immediate: true,
        handler(value) {
          this.validateMessage = value;
          this.validateState = value ? 'error' : '';
        }
      },
      validateStatus(value) {
        this.validateState = value;
      }
    },
    computed: {
      showRequiredAsterisk() {
        return (this.isRequired || this.required) && this.ZmForm && !this.ZmForm.hideRequiredAsterisk;
      },
      labelFor() {
        return this.for || this.prop;
      },
      widthStyle() {
        const ret = {};
        if (this.form.labelPosition === 'top') return ret;
        const labelWidth = this.labelWidth || this.form.labelWidth;
        if (labelWidth) {
          ret.width = labelWidth;
        }
        return ret;
      },
      contentStyle() {
        const ret = {};
        const label = this.label;
        if (this.form.labelPosition === 'top' || this.form.labelPosition === 'inline' || this.form.inline) return ret;
        if (!label && !this.labelWidth && this.isNested) return ret;
        const labelWidth = this.labelWidth || this.form.labelWidth;
        if (labelWidth) {
          ret.marginLeft = labelWidth;
        }
        return ret;
      },
      fieldValue() {
        const model = this.form.model;
        if (!model || !this.prop) {
          return;
        }

        let path = this.prop;
        if (path.indexOf(':') !== -1) {
          path = path.replace(/:/, '.');
        }
        let value = getPropByPath(model, path, true).v;
        if (typeof value === 'string') {
          value = value.trim();
        }

        return value;
      },
      isRequired() {
        let rules = this.getRules();
        let isRequired = false;

        if (rules && rules.length) {
          rules.every(rule => {
            if (rule.required) {
              isRequired = true;
              return false;
            }
            return true;
          });
        }
        return isRequired;
      },
      _formSize() {
        return this.ZmForm.size;
      },
      elFormItemSize() {
        return this.size || this._formSize;
      },
      sizeClass() {
        return this.elFormItemSize || (this.$ELEMENT || {}).size;
      },
      isShowErrorMessage() {
        return this.validateState === 'error' && this.showMessage && this.form.showMessage;
      }
    },
    data() {
      return {
        id: '',
        validateState: '',
        validateMessage: '',
        validateDisabled: false,
        validator: {},
        isNested: false,
        form: null
      };
    },
    methods: {
      validate(trigger, callback = noop) {
        this.validateDisabled = false;
        const rules = this.getFilteredRule(trigger);
        if ((!rules || rules.length === 0) && this.required === undefined) {
          callback();
          return true;
        }

        this.validateState = 'validating';

        const descriptor = {};
        if (rules && rules.length > 0) {
          rules.forEach(rule => {
            delete rule.trigger;
          });
        }
        descriptor[this.prop] = rules;

        const validator = new AsyncValidator(descriptor);
        const model = {};

        model[this.prop] = this.fieldValue;

        validator.validate(model, { firstFields: true }, (errors, invalidFields) => {
          this.validateState = !errors ? 'success' : 'error';
          this.validateMessage = errors ? errors[0].message : '';

          callback(this.validateMessage, invalidFields);
          this.ZmForm && this.ZmForm.$emit('validate', this.prop, !errors, this.validateMessage || null);
        });
      },
      clearValidate() {
        this.validateState = '';
        this.validateMessage = '';
        this.validateDisabled = false;
      },
      resetField() {
        this.validateState = '';
        this.validateMessage = '';

        let model = this.form.model;
        let value = this.fieldValue;
        let path = this.prop;
        if (path.indexOf(':') !== -1) {
          path = path.replace(/:/, '.');
        }

        let prop = getPropByPath(model, path, true);

        this.validateDisabled = true;
        if (Array.isArray(value)) {
          prop.o[prop.k] = [].concat(this.initialValue);
        } else {
          prop.o[prop.k] = this.initialValue;
        }

        this.broadcast('ZmTimeSelect', 'fieldReset', this.initialValue);
      },
      getRules() {
        let formRules = this.form.rules;
        const selfRules = this.rules;
        const requiredRule = this.required !== undefined ? { required: !!this.required } : [];

        const prop = getPropByPath(formRules, this.prop || '');
        formRules = formRules ? (prop.o[this.prop || ''] || prop.v) : [];

        return [].concat(selfRules || formRules || []).concat(requiredRule);
      },
      getFilteredRule(trigger) {
        const rules = this.getRules();

        return rules.filter(rule => {
          if (!this.isShowErrorMessage && this.ZmForm && this.checkTriggerTarget(rule.triggerTarget || this.ZmForm.triggerTarget)) {
            return false;
          }
          if (!rule.trigger || trigger === '') return true;
          if (Array.isArray(rule.trigger)) {
            return rule.trigger.indexOf(trigger) > -1;
          } else {
            return rule.trigger === trigger;
          }
        }).map(rule => objectAssign({}, rule));
      },
      onFieldBlur() {
        this.validate('blur');
        this.ZmForm && (this.ZmForm.relatedTarget = null);
      },
      onFieldChange() {
        if (this.validateDisabled) {
          this.validateDisabled = false;
          return;
        }

        this.validate('change');
        this.ZmForm && (this.ZmForm.relatedTarget = null);
      },
      checkTriggerTarget(id) {
        if (!id) return false;
        const targetEle = document.querySelector(id);
        if (targetEle && (targetEle !== this.ZmForm.relatedTarget && !targetEle.contains(this.ZmForm.relatedTarget))) {
          return true;
        }
      }
    },
    created() {
      let parent = this.$parent;
      let parentName = parent.$options.componentName;
      while (parentName !== 'ZmForm') {
        if (parentName === 'ZmFormItem') {
          this.isNested = true;
        }
        parent = parent.$parent;
        parentName = parent.$options.componentName;
      }
      this.form = parent;
    },
    mounted() {
      if (this.prop) {
        this.dispatch('ZmForm', 'el.form.addField', [this]);

        let initialValue = this.fieldValue;
        if (Array.isArray(initialValue)) {
          initialValue = [].concat(initialValue);
        }
        Object.defineProperty(this, 'initialValue', {
          value: initialValue
        });

        let rules = this.getRules();

        if (rules.length || this.required !== undefined) {
          this.$on('el.form.blur', this.onFieldBlur);
          this.$on('el.form.change', this.onFieldChange);
        }
      }
    },
    beforeDestroy() {
      this.dispatch('ZmForm', 'el.form.removeField', [this]);
    }
  };
</script>

<style lang="scss">
  @import "./form-item";
</style>
